import {
  cloneElement, ReactElement, useEffect, useRef, useState,
} from 'react';

import {
  Checkbox, DatePicker, Dropdown, Form, Radio, RadioGroup, TextArea,
} from '@rrexpress/core';
import { isArray, isEmpty } from 'lodash';

import { FormFieldProps, InputFieldType } from '@/modules/apps/model/form/form-field';
import { IClonedKitbirdDropdownOptionItem } from '@/modules/apps/model/kitbird/dropdown';

import { QueryField } from './query-field';

const kitbirdComponentMap: { [key in InputFieldType]: (props: any) => ReactElement } = {
  form: (props) => <Form {...props} />,
  checkbox: (props) => <Checkbox {...props} />,
  datepicker: (props) => <DatePicker {...props} />,
  dropdown: (props) => <Dropdown {...props} />,
  radiogroup: (props) => <RadioGroup {...props}></RadioGroup>,
  textarea: (props) => <TextArea {...props} />,
  queryfield: (props) => <QueryField {...props} />,
};

const FormField = <T extends InputFieldType>(props: FormFieldProps<T>) => {
  const {
    label, id, component, componentProps, onChange, name, className,
  } = props;
  let { value } = props;

  const FieldComponent = kitbirdComponentMap[component];

  function generateWrappedComponent(targetComponent: ReactElement): ReactElement {
    switch (component) {
      case 'form':
        return cloneElement(targetComponent, {
          id,
          onChange: (e: any) => {
            onChange?.(e?.target?.value);
            value = e?.target?.value;

            targetComponent?.props?.onChange?.(e);
          },
          value: value != null ? value : '',
        });

      /** TODO: Need to separate dropdown and queryfield process */
      case 'dropdown':
      case 'queryfield':
        return (function () {
          const [item, setItem] = useState<IClonedKitbirdDropdownOptionItem | IClonedKitbirdDropdownOptionItem[]>();
          const queryfieldRef = useRef<{ getOptions:() => IClonedKitbirdDropdownOptionItem[] }>();

          // state for handle process of initial value queryfield
          const [isQueryFieldFetched, setIsQueryFieldFetched] = useState(false);

          function renderValueLabel(): string {
            if (targetComponent?.props?.renderValueLabel && !isEmpty(item)) {
              if (typeof targetComponent.props.renderValueLabel === 'function') {
                return targetComponent.props.renderValueLabel(isArray(item) ? item : [item]);
              }
              if (isArray(item)) {
                return item.map((v) => v[targetComponent.props.renderValueLabel as ('label' | 'value')])
                  .reduce((res, v) => {
                    res += `${v}, `;
                    return res;
                  }) as string;
              }
              return item[targetComponent.props.renderValueLabel as ('label' | 'value')] as string;
            }
            if (isArray(item) && item.length > 0) {
              return item.map((v) => v.label)
                .reduce((res, v) => {
                  res += `${v}, `;
                  return res;
                });
            }
            return (item as IClonedKitbirdDropdownOptionItem)?.label || '';
          }

          /**
           * Set initial Value for Dropdown
           */
          useEffect(() => {
            if (component === 'dropdown') {
              let item;

              if (isArray(value)) {
                item = value.map((v: string) => {
                  const isExistInOption = targetComponent?.props?.options?.find(
                    (opt: IClonedKitbirdDropdownOptionItem) => (opt?.value === v),
                  );

                  if (isExistInOption) {
                    return isExistInOption.value;
                  }
                  return { label: v, value: v };
                });
              } else {
                const isExistInOption = targetComponent?.props?.options?.find(
                  (opt: IClonedKitbirdDropdownOptionItem) => (opt?.value === value),
                );

                if (isExistInOption) {
                  item = isExistInOption;
                } else {
                  item = { label: value, value };
                }
              }

              setItem(item);
            }
          }, [value]);

          /**
           * Set initial value for Queryfield
           */
          useEffect(() => {
            if (component === 'queryfield' && queryfieldRef.current && !isQueryFieldFetched) {
              const options = queryfieldRef.current.getOptions();
              let item;

              if (isArray(value)) {
                item = value.map((v: string) => {
                  const isExistInOption = options.find(
                    (opt: IClonedKitbirdDropdownOptionItem) => (opt?.value === v),
                  );

                  if (isExistInOption) {
                    return isExistInOption;
                  }
                  return { label: v, value: v };
                });
              } else {
                const isExistInOption = options.find(
                  (opt: IClonedKitbirdDropdownOptionItem) => (opt?.value === value),
                );

                if (isExistInOption) {
                  item = isExistInOption;
                } else {
                  item = { label: value, value };
                }
              }

              setItem(item);
              if (options.length) setIsQueryFieldFetched(true);
            }
          }, [isQueryFieldFetched, queryfieldRef.current]);

          return (
            <>
              {
                component === 'dropdown' ? (
                  <Dropdown
                    id={id}
                    {...targetComponent?.props}
                    value={renderValueLabel()}
                    onSelected={(item: IClonedKitbirdDropdownOptionItem | IClonedKitbirdDropdownOptionItem[]) => {
                      let finalItem;

                      if (isArray(item) && targetComponent?.props?.onSelectedOutputKey) {
                        finalItem = item.map((v) => v[targetComponent?.props?.onSelectedOutputKey as 'label' | 'value']);
                      } else if (targetComponent?.props?.onSelectedOutputKey) {
                        finalItem = (item as IClonedKitbirdDropdownOptionItem)[targetComponent?.props?.onSelectedOutputKey as 'label' | 'value'];
                      } else {
                        finalItem = item;
                      }

                      onChange?.(finalItem);
                      setItem(item);

                      targetComponent?.props?.onSelected?.(item);
                    }}
                  />
                ) : (
                  <QueryField
                    id={id}
                    {...targetComponent?.props}
                    value={renderValueLabel()}
                    onSelected={(item: IClonedKitbirdDropdownOptionItem | IClonedKitbirdDropdownOptionItem[]) => {
                      let finalItem;

                      if (isArray(item) && targetComponent?.props?.onSelectedOutputKey) {
                        finalItem = item.map((v) => v[targetComponent?.props?.onSelectedOutputKey as 'label' | 'value']);
                      } else if (targetComponent?.props?.onSelectedOutputKey) {
                        finalItem = (item as IClonedKitbirdDropdownOptionItem)[targetComponent?.props?.onSelectedOutputKey as 'label' | 'value'];
                      } else {
                        finalItem = item;
                      }

                      onChange?.(finalItem);
                      setItem(item);

                      targetComponent?.props?.onSelected?.(item);
                    }}
                    ref={queryfieldRef}
                  />
                )
              }
            </>
          );
        }());
      case 'checkbox':
        return (function () {
          const [isChecked, setIsChecked] = useState<boolean>(false);

          useEffect(() => {
            if (targetComponent?.props?.checked != null) {
              setIsChecked(targetComponent.props.checked);
              onChange?.(targetComponent.props.checked);
            }
          }, []);

          return (
            <>
              <Checkbox
                id={id}
                {...targetComponent?.props}
                checked={isChecked}
                onChange={(e: any) => {
                  onChange?.(e?.target?.checked);
                  setIsChecked(e?.target?.checked);

                  targetComponent?.props?.onChange?.(e);
                }}
              />
            </>
          );
        }());
      case 'datepicker':
        return cloneElement(targetComponent, {
          id,
          onChange: (date: any) => {
            onChange?.(date);

            targetComponent?.props?.onChange?.(date);
          },
          onSubmit: (date: any) => {
            onChange?.(date);

            targetComponent?.props?.onSubmit?.(date);
          },
          onReset: () => {
            onChange?.(null);

            targetComponent?.props?.onReset?.();
          },
          value: value?.length ? value : targetComponent?.props?.value?.length ? targetComponent?.props?.value : null,
        });
      case 'textarea':
        return cloneElement(targetComponent, {
          id,
          onChange: (e: any) => {
            onChange?.(e?.target?.value);

            targetComponent?.props?.onChange?.(e);
          },
          value: value || (targetComponent?.props?.value ? targetComponent?.props?.value : null),
        });
      case 'radiogroup':
        return (function () {
          return (
            <>
              <RadioGroup
                {...targetComponent?.props}
                id={id}
              >
                {targetComponent?.props?.radioButtonsProps?.map((radioProps: any, i: number) => (
                  <Radio
                    key={i}
                    {...radioProps}
                    name={name}
                    onChange={(e) => {
                      onChange?.(e?.target?.value);

                      radioProps?.onChange?.(e);
                    }}
                    selected={value || (targetComponent?.props?.value ? targetComponent?.props?.value : '')}
                  />
                ))}
              </RadioGroup>
            </>
          );
        }());
        return cloneElement(targetComponent, {
          id,
          onChange: (data: number) => {
            onChange?.(data);
            value = data;

            targetComponent?.props?.onChange?.(data);
          },
          value: value != null ? value : targetComponent?.props?.value ? targetComponent?.props?.value : 0,
        });
      default:
        return <></>;
    }
  }

  return (
    <div className={`flex items-center flex-row ${className || ''}`}>
      {
        label && (
          <div className="text-sm text-wrap w-32">
            {label}
            {
              componentProps?.required && (
                <span className="ml-1 text-red-500">*</span>
              )
            }
          </div>
        )
      }
      <div className="flex-auto">{generateWrappedComponent(FieldComponent({ ...componentProps, id: componentProps?.id || name }))}</div>
    </div>
  );
};

// this is an important code to identify FormField as FormField after build minification,
// will be used inside BaseForm component
// if you found another better solution, please refactor
FormField.code = 'FormField';

export default FormField;
